import api from './api';
import { Transaction, ApiResponse, PaginatedResponse } from '../types';

class TransactionService {
  async getTransactions(
    page = 1, 
    limit = 10, 
    customerId?: number, 
    filters?: { spop_id?: string; [key: string]: any }
  ): Promise<PaginatedResponse<Transaction>> {
    let url = `/transactions?page=${page}&limit=${limit}`;
    if (customerId) {
      url += `&customerId=${customerId}`;
    }
    // Add additional filters
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }
    const response = await api.get<PaginatedResponse<Transaction>>(url);
    return response.data;
  }

  async getTransaction(id: number): Promise<Transaction> {
    const response = await api.get<ApiResponse<Transaction>>(`/transactions/${id}`);
    return response.data.data;
  }

  async createTransaction(transactionData: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'Customer' | 'Package'>): Promise<Transaction> {
    const response = await api.post<ApiResponse<Transaction>>('/transactions', transactionData);
    return response.data.data;
  }

  async updateTransaction(id: number, transactionData: Partial<Transaction>): Promise<Transaction> {
    const response = await api.put<ApiResponse<Transaction>>(`/transactions/${id}`, transactionData);
    return response.data.data;
  }

  async deleteTransaction(id: number): Promise<void> {
    await api.delete(`/transactions/${id}`);
  }

  async updateTransactionStatus(
    id: string,
    data: { status: 'pending' | 'completed' | 'failed' | 'cancelled'; notes?: string }
  ): Promise<Transaction> {
    const response = await api.patch<ApiResponse<Transaction>>(`/transactions/${id}/status`, data);
    return response.data.data;
  }

  async getTransactionStats(filters?: {
    spop_id?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<{
    success: boolean;
    data: {
      statusStats: Array<{
        status: string;
        count: number;
        total_amount: number;
      }>;
      typeStats: Array<{
        type: string;
        count: number;
        total_amount: number;
      }>;
      dailyStats: Array<{
        date: string;
        count: number;
        total_amount: number;
      }>;
    };
  }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const query = params.toString();
    const url = query ? `/transactions/stats?${query}` : '/transactions/stats';
    const response = await api.get(url);
    return response.data;
  }

  async getRevenueChart(period: 'daily' | 'weekly' | 'monthly' = 'daily', filters?: Record<string, any>): Promise<{
    data: {
      period: string;
      chartData: Array<{
        date: string;
        revenue: number;
        transaction_count: number;
      }>;
      totalRevenue: number;
      totalTransactions: number;
    };
  }> {
    const params = new URLSearchParams({ period });
    
    if (filters?.spop_id) {
      params.append('spop_id', filters.spop_id);
    }
    
    const response = await api.get(`/transactions/revenue-chart?${params.toString()}`);
    return response.data;
  }
}

const transactionService = new TransactionService();
export default transactionService;
